import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useUIStore = defineStore('ui', () => {
  // Dark mode state
  const darkMode = ref(false)
  const isInitialized = ref(false)

  // Computed
  const theme = computed(() => darkMode.value ? 'dark' : 'light')

  // Initialize dark mode from localStorage or system preference
  const initializeDarkMode = () => {
    if (isInitialized.value) return

    const stored = localStorage.getItem('dorilon-dark-mode')

    if (stored !== null) {
      darkMode.value = stored === 'true'
    } else {
      // Check system preference
      darkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches
    }

    // Apply dark mode class
    applyDarkMode()
    isInitialized.value = true
  }

  // Toggle dark mode
  const toggleDarkMode = () => {
    darkMode.value = !darkMode.value
    localStorage.setItem('dorilon-dark-mode', String(darkMode.value))
    applyDarkMode()
  }

  // Set dark mode explicitly
  const setDarkMode = (value) => {
    darkMode.value = value
    localStorage.setItem('dorilon-dark-mode', String(value))
    applyDarkMode()
  }

  // Apply dark mode to document
  const applyDarkMode = () => {
    if (darkMode.value) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // Watch for system preference changes
  const watchSystemPreference = () => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e) => {
      // Only update if user hasn't set a manual preference
      const stored = localStorage.getItem('dorilon-dark-mode')
      if (stored === null) {
        darkMode.value = e.matches
        applyDarkMode()
      }
    }

    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }

  return {
    darkMode,
    theme,
    isInitialized,
    initializeDarkMode,
    toggleDarkMode,
    setDarkMode,
    watchSystemPreference
  }
})
